import type { Configuration } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-browser';

/**
 * Configuração do MSAL.js para login SSO contra o Microsoft Entra ID da Vega Asset.
 *
 * O tenant é único (Vega Asset Management). Já o clientId do SPA e o scope da API
 * variam por ambiente: em dev usam os defaults embutidos abaixo; em produção são
 * injetados em runtime via `window.__APP_CONFIG__` (config.js gerado pelo pipeline,
 * carregado antes do bundle no index.html). Ver `resolveClientId`/`resolveApiScope`.
 */
export const TENANT_ID = 'b565008b-660a-4935-9ef2-4d660fce559f';

/** Default de dev: App Registration "Pipeline Vega - SPA" (dev, criada em 2026-05-27). */
export const SPA_CLIENT_ID = 'f98b8633-12d2-4a60-9c79-519b01137b63';

/**
 * Default de dev: scope da App Registration "Pipeline Vega - API" (dev). Formato canônico:
 * `api://<api-app-id-uri>/<scope>`. Ver docs/entra-id-config.md.
 */
export const API_SCOPE = 'api://2b660444-3c9f-4458-8d20-a6147910b1e7/access_as_user';

/**
 * clientId efetivo: o do config.js de runtime quando presente (prod), senão o default de dev.
 */
export function resolveClientId(): string {
  return window.__APP_CONFIG__?.clientId || SPA_CLIENT_ID;
}

/**
 * scope da API efetivo: o do config.js de runtime quando presente (prod), senão o default de dev.
 */
export function resolveApiScope(): string {
  return window.__APP_CONFIG__?.apiScope || API_SCOPE;
}

export const apiRequestScopes = [resolveApiScope()];

export const msalConfig: Configuration = {
  auth: {
    clientId: resolveClientId(),
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL]', message);
            return;
          case LogLevel.Warning:
            console.warn('[MSAL]', message);
            return;
          default:
            return;
        }
      },
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Scopes pedidos no fluxo de login (não para a API — apenas identidade).
 */
export const loginRequestScopes = ['openid', 'profile', 'email', 'User.Read'];
